import type { ContentDatabase } from "@-/dev.improve.haniverse.database.content";
import { ContentDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.content";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { uniq } from "@-/com.npmjs.array-utils-ts";
import { contentQueries } from "@-/dev.improve.haniverse.database.schemas.content.query";
import {
  createUseDataloader,
  type QueryKeyStableDataloader,
} from "@-/dev.improve.haniverse.lib.dataloader";
import { queryOptions, useQueryClient } from "@-/com.npmjs.tanstack__react-query";
import { useCallback } from "react";
import { hasLength } from "@-/com.npmjs.ts-array-length";

/** grapheme → its tone-number pinyin readings (e.g. `好 → ["hao3"]`), in row order. */
export type GraphemePinyinLookupMap = ReadonlyMap<string, readonly string[]>;

/**
 * Group tone-number `pinyin` readings by grapheme from a set of pronunciation-card rows. A grapheme
 * may have multiple rows (multiple readings); row order is preserved. Graphemes with no rows are
 * simply absent from the map. Shared by both the raw-`contentDb` reader below and the `contentQueries`
 * loader path.
 */
function groupPinyinRowsByGrapheme(
  rows: readonly { grapheme: string; pinyin: string }[],
): GraphemePinyinLookupMap {
  const map = new Map<string, string[]>();
  for (const row of rows) {
    const list = map.get(row.grapheme) ?? [];
    list.push(row.pinyin);
    map.set(row.grapheme, list);
  }
  return map;
}

/**
 * Read every pronunciation card for the given graphemes in a single content-DB query and group the
 * tone-number `pinyin` readings by grapheme. A grapheme may have multiple rows (multiple readings);
 * row order is preserved. Graphemes with no pronunciation cards are simply absent from the map.
 */
export async function readGraphemePinyinLookup(
  contentDb: ContentDatabase,
  graphemes: readonly string[],
): Promise<GraphemePinyinLookupMap> {
  const uniqueGraphemes = uniq([...graphemes]);
  if (hasLength(uniqueGraphemes, 0)) {
    return new Map();
  }

  const rows = await contentDb(import.meta.location).query.appGraphemePronunciationCard.findMany({
    columns: { grapheme: true, pinyin: true },
    where: { grapheme: { in: uniqueGraphemes } },
  });

  return groupPinyinRowsByGrapheme(rows);
}

const GRAPHEME_PINYIN_QUERY_KEY_PREFIX = ["graphemePinyin"] as const;

export const EMPTY_PINYIN_LIST: readonly string[] = [];

function useLoader() {
  const client = useQueryClient();
  const [contentDb] = useContextSelectors(ContentDatabaseContext, (context) => context.db);
  const getPinyinQueryKey = contentQueries(
    import.meta.location,
  ).appGraphemePronunciationCard.findMany.useGetQueryKey({});

  const batchLoad = useCallback(
    async (graphemes: readonly string[]) => {
      const uniqueGraphemes = uniq([...graphemes]);
      const rows = hasLength(uniqueGraphemes, 0)
        ? []
        : await client.fetchQuery(
            contentQueries(
              import.meta.location,
            ).appGraphemePronunciationCard.findMany.getQueryOptions(getPinyinQueryKey, {
              where: { grapheme: { in: uniqueGraphemes } },
            }),
          );
      const map = groupPinyinRowsByGrapheme(rows);
      return graphemes.map((g) => map.get(g) ?? EMPTY_PINYIN_LIST);
    },
    [client, getPinyinQueryKey],
  );
  return { batchLoad, cacheIdentity: [client, contentDb] as const };
}

const useGraphemePinyinLoader = createUseDataloader({ useLoader });

function buildGraphemePinyinQueryOptions(
  loader: QueryKeyStableDataloader<string, readonly string[]>,
  grapheme: string,
) {
  return queryOptions({
    queryFn: () => loader.load(grapheme),
    // `loader` is a `QueryKeyStableDataloader`: its `toJSON()` returns a stable
    // per-db tag, so embedding it here hashes consistently (no refetch storm)
    // while still discriminating cache entries by content-DB identity.
    queryKey: [...GRAPHEME_PINYIN_QUERY_KEY_PREFIX, grapheme, loader] as const,
  });
}

type GraphemePinyinQueryOptions = ReturnType<typeof buildGraphemePinyinQueryOptions>;

/**
 * Hook returning `queryOptions` for per-grapheme tone-number pinyin readings.
 * Each grapheme is its own Query cache entry; consumers querying in the same
 * tick coalesce into one batched content-DB query via DataLoader.
 *
 * The content DB is immutable for the app's lifetime, so the query key does
 * not include a change-version.
 */
export function useGraphemePinyinQueryOptionsList(
  graphemes: readonly string[],
): GraphemePinyinQueryOptions[] {
  const loader = useGraphemePinyinLoader();

  return graphemes.map((grapheme) => buildGraphemePinyinQueryOptions(loader, grapheme));
}
